import GatewayService from '@/service/gateway-service';
import { ACCOUNT, CUSTOMER } from '@/pages/account/gql';
import {
  AccountCountryCodes,
  AccountSessionStorageTypes
} from '@/pages/account/constants';
import RechargeGateway from '@/gateway/Recharge';
import { RechargeAddress } from '@/gateway/Recharge/constants';
import { convertSnakeCaseToWords } from '@/utils';
import AwsCloudGateway from '@/gateway/AwsCloud';

export default class AccountService {
  static addresses = {};

  static addEventListenersToRequiredInputTextFields(parentForm) {
    let errors = [];
    const submitButton = parentForm.querySelector('#address-submit-btn');

    const requiredInputFields = [
      {
        name: 'first_name',
        element: parentForm.querySelector("input[name='address[first_name]']")
      },
      {
        name: 'last_name',
        element: parentForm.querySelector("input[name='address[last_name]']")
      },
      {
        name: 'address1',
        element: parentForm.querySelector("input[name='address[address1]']")
      },
      {
        name: 'city',
        element: parentForm.querySelector("input[name='address[city]']")
      },
      {
        name: 'zip',
        element: parentForm.querySelector("input[name='address[zip]']")
      },

      {
        name: 'TaxId',
        element: parentForm.querySelector("input[name='address[TaxId]']")
      }
    ];

    requiredInputFields.forEach((item) => {
      item.element.onchange = (event) => {
        const taxFieldPresent = parentForm.querySelector('.taxIdField');

        if (taxFieldPresent.style.display === 'none') {
          errors = errors.filter((x) => x !== 'TaxId');
          if (item.name === 'TaxId') {
            return;
          }
        }

        if (event.target.value === '') {
          const newElement = document.createElement('p');
          newElement.textContent = 'REQUIRED';
          newElement.classList.add('base-font-7');
          newElement.classList.add('address-error-text-input');

          event.target.parentNode.appendChild(newElement);

          errors.push(item.name);
          if (errors.length > 0) {
            submitButton.disabled = true;
          }
        } else {
          const errorElement = event.target.parentNode.querySelector(
            '.address-error-text-input'
          );

          event.target.parentNode.removeChild(errorElement);
          errors = errors.filter((x) => x !== item.name);
          if (errors.length === 0) {
            submitButton.disabled = false;
          }
        }
      };
    });
  }

  static async getCustomerToken({ email, pass }) {
    const token = await GatewayService.getStoreFront().getRequest({
      query: ACCOUNT,
      variables: { email, pass }
    });

    token?.customerAccessTokenCreate?.customerAccessToken?.accessToken &&
      ORW.utilities.setCookie(
        AccountSessionStorageTypes.fobClientToken,
        token?.customerAccessTokenCreate?.customerAccessToken?.accessToken,
        1
      );

    return token;
  }
  static async getCustomer() {
    const token = ORW.utilities.getCookie(
      AccountSessionStorageTypes.fobClientToken
    );

    if (!token || token === '') window.location.href = '/account/logout';

    const result = await GatewayService.getStoreFront().getRequest({
      query: CUSTOMER,
      variables: { token }
    });

    return result.customer;
  }

  static getAddressListFromCustomer(customer) {
    return customer?.addresses?.edges?.map((x) => ({ ...x.node })) ?? [];
  }

  static getUnifiedAddressesFromRechargeAndShopify({
    shopifyAddresses,
    rechargeAddresses
  }) {
    const toDelete = [];

    const unified = rechargeAddresses
      .map((reAddr) => {
        const shopifyAddress = shopifyAddresses.find((shAddr) =>
          AccountService.areAddressesEqual({
            shopifyAddress: shAddr,
            rechargeAddress: reAddr
          })
        );

        if (shopifyAddress) {
          return {
            shopifyAddress,
            rechargeAddress: reAddr,
            shopId: shopifyAddress.id
          };
        }

        toDelete.push(reAddr);

        return undefined;
      })
      .filter((x) => x)
      .reduce((acc, curr) => {
        return {
          ...acc,
          [curr.shopId]: curr
        };
      }, {});

    const shopifyAddressOutsideRecharge = shopifyAddresses
      .filter((x) => !unified[x.id])
      .reduce(
        (acc, curr) => ({
          ...acc,
          [curr.id]: curr
        }),
        {}
      );

    AccountService.addresses = {
      shopifyAddressOutsideRecharge,
      unifiedAddresses: unified,
      rechargeAddressesToDelete: toDelete
    };

    return AccountService.addresses;
  }

  static areAddressesEqual({ shopifyAddress, rechargeAddress }) {
    return (
      shopifyAddress.address1 === rechargeAddress.address1 &&
      shopifyAddress.address2 === rechargeAddress.address2 &&
      shopifyAddress.city === rechargeAddress.city &&
      shopifyAddress.company === rechargeAddress.company &&
      shopifyAddress.country_code === rechargeAddress.country_code &&
      shopifyAddress.zip === rechargeAddress.zip &&
      shopifyAddress.province === rechargeAddress.province &&
      shopifyAddress.first_name === rechargeAddress.first_name &&
      shopifyAddress.last_name === rechargeAddress.last_name
    );
  }

  static getCheckedRechargeAddressObject(addressObject) {
    const result = { ...addressObject };
    for (let key in result) {
      if (result[key] === '' || result[key] === undefined) {
        delete result[key];
      }
    }

    return result;
  }

  static getRechargeAddressObjectFromHtmlContainer(
    parentForm,
    originalRechargeAddress
  ) {
    const firstName =
      parentForm.querySelector("input[name='address[first_name]']").value ||
      undefined;
    const lastName =
      parentForm.querySelector("input[name='address[last_name]']").value ||
      undefined;
    const company =
      parentForm.querySelector("input[name='address[company]']").value ||
      undefined;
    const address1 =
      parentForm.querySelector("input[name='address[address1]']").value ||
      undefined;
    const address2 =
      parentForm.querySelector("input[name='address[address2]']").value ||
      undefined;
    const city =
      parentForm.querySelector("input[name='address[city]']").value ||
      undefined;
    const country =
      parentForm.querySelector("select[name='address[country]']").value ||
      undefined;
    const zip =
      parentForm.querySelector("input[name='address[zip]']").value || undefined;
    const phone =
      parentForm.querySelector("input[name='address[phone]']").value ||
      undefined;
    const province =
      parentForm.querySelector("select[name='address[province]']").value ||
      undefined;

    const taxId =
      parentForm.querySelector("input[name='address[TaxId]']").value ||
      undefined;

    const orderAttributes = originalRechargeAddress?.order_attributes;

    if (taxId) {
      const taxIdObject = orderAttributes?.find((x) => x.name === 'tax_id');
      taxIdObject
        ? (taxIdObject.value = taxId)
        : orderAttributes?.push({ name: 'tax_id', value: taxId });
    }

    return {
      address1: address1,
      address2: address2,
      city: city,
      company: company,
      country_code: AccountCountryCodes[country],
      country: country,
      first_name: firstName,
      last_name: lastName,
      province,
      zip,
      phone,
      order_attributes: orderAttributes
    };
  }

  static async submitAddressObjectToRecharge({
    updateAddressObject,
    addressId,
    setAsDefault
  }) {
    if (Object.keys(AccountService.addresses).length === 0) return;

    const shopifyAddr =
      AccountService.addresses.shopifyAddressOutsideRecharge[addressId];

    const unifiedAddr = AccountService.addresses.unifiedAddresses[addressId];

    try {
      let rechargeResult = {};
      if (shopifyAddr) {
        rechargeResult =
          await RechargeGateway.createAddress(updateAddressObject);
      }

      if (unifiedAddr) {
        rechargeResult = await RechargeGateway.updateAddress(
          updateAddressObject,
          unifiedAddr.rechargeAddress.id
        );
      }

      // set address as default
      if (setAsDefault) {
        const rechargeAddressId = rechargeResult.id;
        await AccountService.setRechargeAddressAsDefault(rechargeAddressId);
      }

      return rechargeResult;
    } catch (e) {
      if (e.message) {
        const message = JSON.parse(e.message);
        return { ...e, errorMessage: message };
      }
      return { ...e, error: e };
    }
  }

  static async updateRechargeAddress({
    updateAddressObject,
    rechargeAddressId,
    setAsDefault
  }) {
    try {
      const updatedResult = await RechargeGateway.updateAddress(
        updateAddressObject,
        rechargeAddressId
      );

      // set address as default
      if (setAsDefault) {
        await AccountService.setRechargeAddressAsDefault(rechargeAddressId);
      }

      console.log('===== updatedResult', updatedResult);
      return { updatedResult };
    } catch (e) {
      console.log('==== error ', e);

      if (e.message) {
        return { ...e, errorMessage: e.message };
      }
      return { ...e, error: e };
    }
  }

  static async createRechargeAddress({ createAddressObject, setAsDefault }) {
    try {
      const createdResult =
        await RechargeGateway.createAddress(createAddressObject);

      // set address as default
      if (setAsDefault) {
        await AccountService.setRechargeAddressAsDefault(createdResult.id);
      }

      console.log('===== createdResult', createdResult);
      return { createdResult };
    } catch (e) {
      if (e.message) {
        const message = JSON.parse(e.message);
        return { ...e, errorMessage: message };
      }
      return { ...e, error: e };
    }
  }

  static async setRechargeAddressAsDefault(addressId) {
    const maxNumOfSubsPerRequest = 20;
    if (addressId) {
      try {
        const subscriptions = await RechargeGateway.getSubscriptions();
        const subsToUpdate = subscriptions.map((sub) => {
          return {
            id: sub.id,
            data: {
              address_id: addressId
            }
          };
        });

        const numberOfChunks = Math.ceil(
          subsToUpdate.length / maxNumOfSubsPerRequest
        );
        let chunkStart = 0;

        for (let i = 0; i < numberOfChunks; i++) {
          let chunkEnd = Math.min(
            chunkStart + maxNumOfSubsPerRequest,
            subsToUpdate.length
          );
          console.log(
            `Processing chunk ${
              i + 1
            }/${numberOfChunks}: from index ${chunkStart} to ${chunkEnd}`
          );

          await AwsCloudGateway.cancelUpdateAndCreateSubscription(
            subsToUpdate.slice(chunkStart, chunkEnd),
            [],
            []
          );
          chunkStart = chunkEnd;
        }
      } catch (e) {
        console.log('==== error ', e);
      }
    }
  }

  static getAddressErrorsFromErrorObject(errorObject) {
    if (!errorObject) return [];

    const errorList = Object.values(errorObject)
      .reduce((acc, curr) => {
        console.log('===== curr ', curr);

        return [...acc, curr];
      }, [])
      .map((x) => convertSnakeCaseToWords(Array.isArray(x) ? x[0] : x));

    console.log('===== errorList', errorList);

    return errorList;

    if (errorObject.zip) {
      errorList.push(errorObject.zip[0]);
    }
    if (errorObject.all) {
      errorList.push(
        `${errorObject.all}: Please verify your address because it seems to not exist`
      );
    }
    if (errorObject._schema) {
      const errorString = errorObject._schema[0];

      errorList.push(errorString);
    }

    return errorList;
  }
}
